import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage, getApiFieldErrors } from "../../lib/apiError";
import { useUsersLoginCreate, useUsersVerifyTwoFactorCreate } from "../../services/auth/generated/users/users";
import { useAuthStore } from "../../store/authStore";
import "./AuthPage.css";

/** Login response shape. The backend returns either a session envelope
 * or a 2FA challenge envelope; orval doesn't model the discrimination
 * (one operationId, one declared response), so we accept the union and
 * branch on `twoFactorRequired`. */
type LoginResponse =
  | {
      twoFactorRequired: true;
      challengeToken: string;
    }
  | {
      twoFactorRequired?: false;
      profile?: { id?: number; email?: string; firstName?: string; lastName?: string };
      isProfileComplete?: boolean;
    };

const B2BLoginPage = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  // 2FA state. `challengeToken` doubles as the view discriminator —
  // when set, the form swaps to the code-entry view; cleared when the
  // user backs out or finishes a successful verify.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);

  /** Common post-login handoff: pluck the profile out and route. */
  const finishLogin = (data: {
    profile?: { id?: number; email?: string; firstName?: string; lastName?: string };
    isProfileComplete?: boolean;
  }) => {
    if (data?.profile) {
      const profile = data.profile;
      setAuth(
        {
          id: profile.id ?? 0,
          email: profile.email ?? "",
          firstName: profile.firstName,
          lastName: profile.lastName,
        },
        data.isProfileComplete ?? false,
      );
    }
    navigate("/b2b/dashboard");
  };

  // Use generated TanStack Query mutation hook
  const loginMutation = useUsersLoginCreate({
    mutation: {
      onSuccess: (raw) => {
        const data = raw as unknown as LoginResponse;
        if (data && "twoFactorRequired" in data && data.twoFactorRequired === true) {
          setChallengeToken(data.challengeToken);
          setTwoFactorError(null);
          setCode("");
          setUseRecoveryCode(false);
          return;
        }
        // After narrowing out the 2FA branch, the remaining shape has the profile.
        finishLogin(
          data as {
            profile?: { id?: number; email?: string; firstName?: string; lastName?: string };
            isProfileComplete?: boolean;
          },
        );
      },
      onError: (error) => {
        // Field errors (e.g. malformed email) go next to the inputs; anything
        // else — "Invalid email or password", "Your company is awaiting
        // approval." (403), throttling, 5xx — goes to the banner.
        const fieldErrors = getApiFieldErrors(error);
        const nextErrors: { email?: string; password?: string } = {
          email: fieldErrors.email,
          password: fieldErrors.password,
        };
        setErrors(nextErrors);
        if (!nextErrors.email && !nextErrors.password) {
          setSubmitError(getApiErrorMessage(error, t("b2bLogin.errors.loginFailed")));
        }
      },
    },
  });

  const verifyMutation = useUsersVerifyTwoFactorCreate({
    mutation: {
      onSuccess: (raw) => {
        // The verify response always carries the post-login envelope
        // (the 2FA challenge can't recurse into another challenge).
        finishLogin(
          raw as unknown as {
            profile?: { id?: number; email?: string; firstName?: string; lastName?: string };
            isProfileComplete?: boolean;
          },
        );
      },
      onError: (error) => {
        setTwoFactorError(getApiErrorMessage(error, t("shared.twoFactor.errors.invalidCode")));
      },
    },
  });

  const handleBackClick = () => navigate("/");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    const nextErrors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      nextErrors.email = t("shared.errors.enterEmail");
    }

    if (!password.trim()) {
      nextErrors.password = t("shared.errors.enterPassword");
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    // Call the mutation with the login data
    loginMutation.mutate({
      data: {
        email: email.trim(),
        password: password,
      },
    });
  };

  const handleVerifySubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTwoFactorError(null);

    const trimmed = code.trim();
    if (!trimmed) {
      setTwoFactorError(
        useRecoveryCode ? t("shared.twoFactor.errors.enterRecovery") : t("shared.twoFactor.errors.enterCode"),
      );
      return;
    }
    if (!challengeToken) {
      setTwoFactorError(t("shared.twoFactor.errors.sessionExpired"));
      return;
    }

    verifyMutation.mutate({
      data: useRecoveryCode ? { challengeToken, recoveryCode: trimmed } : { challengeToken, code: trimmed },
    });
  };

  const cancelTwoFactor = () => {
    setChallengeToken(null);
    setCode("");
    setUseRecoveryCode(false);
    setTwoFactorError(null);
  };

  return (
    <div className="auth-page">
      <div className="auth-page__background" />

      <div className="container">
        <div className="auth-page__inner">
          <button type="button" className="auth-page__back-link" onClick={handleBackClick}>
            {t("shared.backToPlatformSelection")}
          </button>

          <section className="auth-card" aria-label={t("b2bLogin.ariaLabel")}>
            <div className="auth-card__icon-wrapper">
              <div className="auth-card__icon auth-card__icon--primary">
                <span className="auth-card__icon-glyph" aria-hidden="true">
                  <i className="fa-solid fa-building-columns" />
                </span>
              </div>
            </div>

            <header className="auth-card__header">
              <h1 className="auth-card__title">{challengeToken ? t("shared.twoFactor.title") : t("b2bLogin.title")}</h1>
              <p className="auth-card__subtitle">
                {challengeToken
                  ? useRecoveryCode
                    ? t("shared.twoFactor.subtitleRecovery")
                    : t("shared.twoFactor.subtitleCode")
                  : t("b2bLogin.subtitle")}
              </p>
            </header>

            {challengeToken ? (
              <form className="auth-form" onSubmit={handleVerifySubmit}>
                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="b2b-2fa-code">
                    {useRecoveryCode ? t("shared.twoFactor.recoveryLabel") : t("shared.twoFactor.codeLabel")}
                  </label>
                  <div
                    className={`auth-form__input-wrapper ${twoFactorError ? "auth-form__input-wrapper--error" : ""}`}
                  >
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-solid fa-shield-halved" />
                    </span>
                    <input
                      id="b2b-2fa-code"
                      type="text"
                      className="auth-form__input"
                      placeholder={useRecoveryCode ? "xxxx-xxxx" : "123456"}
                      // Phones lift the numeric pad for TOTP. Recovery codes
                      // are alphanumeric so we leave that decision to the OS.
                      inputMode={useRecoveryCode ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      autoFocus
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      required
                    />
                  </div>
                </div>

                {twoFactorError && (
                  <p className="auth-form__error auth-form__error--block" role="alert">
                    {twoFactorError}
                  </p>
                )}

                <button
                  type="submit"
                  className="auth-form__submit auth-form__submit--primary"
                  disabled={verifyMutation.isPending}
                >
                  {verifyMutation.isPending ? t("shared.twoFactor.verifying") : t("shared.twoFactor.verify")}
                </button>

                <div className="auth-form__divider">
                  <span>{t("shared.or")}</span>
                </div>

                <button
                  type="button"
                  className="auth-form__link"
                  onClick={() => {
                    setUseRecoveryCode((prev) => !prev);
                    setCode("");
                    setTwoFactorError(null);
                  }}
                >
                  {useRecoveryCode ? t("shared.twoFactor.useAuthenticator") : t("shared.twoFactor.useRecovery")}
                </button>

                <p className="auth-form__footer-text">
                  <button type="button" className="auth-form__link auth-form__link--muted" onClick={cancelTwoFactor}>
                    {t("shared.backToSignIn")}
                  </button>
                </p>
              </form>
            ) : (
              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="b2b-email">
                    {t("shared.emailLabel")}
                  </label>
                  <div className={`auth-form__input-wrapper ${errors.email ? "auth-form__input-wrapper--error" : ""}`}>
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-regular fa-envelope" />
                    </span>
                    <input
                      id="b2b-email"
                      type="email"
                      className="auth-form__input"
                      placeholder={t("shared.emailPlaceholder")}
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />
                  </div>
                  {errors.email ? <p className="auth-form__error">{errors.email}</p> : null}
                </div>

                <div className="auth-form__field">
                  <label className="auth-form__label" htmlFor="b2b-password">
                    {t("shared.passwordLabel")}
                  </label>
                  <div
                    className={`auth-form__input-wrapper auth-form__input-wrapper--password ${
                      errors.password ? "auth-form__input-wrapper--error" : ""
                    }`}
                  >
                    <span className="auth-form__input-icon" aria-hidden="true">
                      <i className="fa-solid fa-lock" />
                    </span>
                    <input
                      id="b2b-password"
                      type={showPassword ? "text" : "password"}
                      className="auth-form__input"
                      placeholder={t("shared.passwordPlaceholder")}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="auth-form__password-toggle"
                      aria-label={showPassword ? t("shared.hidePassword") : t("shared.showPassword")}
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <i className={showPassword ? "fa-regular fa-eye-slash" : "fa-regular fa-eye"} />
                    </button>
                  </div>
                  {errors.password ? <p className="auth-form__error">{errors.password}</p> : null}
                </div>

                {submitError && (
                  <p className="auth-form__error auth-form__error--block" role="alert">
                    {submitError}
                  </p>
                )}

                <div className="auth-form__meta-row">
                  <label className="auth-form__checkbox">
                    <input type="checkbox" />
                    <span>{t("shared.rememberMe")}</span>
                  </label>
                  <button
                    type="button"
                    className="auth-form__link auth-form__link--small"
                    onClick={() => navigate("/b2b/forgot-password")}
                  >
                    {t("shared.forgotPassword")}
                  </button>
                </div>

                <button
                  type="submit"
                  className="auth-form__submit auth-form__submit--primary"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? t("shared.signingIn") : t("shared.signIn")}
                </button>

                <div className="auth-form__divider">
                  <span>{t("shared.or")}</span>
                </div>

                <p className="auth-form__footer-text">
                  {t("b2bLogin.noAccount")}{" "}
                  <button type="button" className="auth-form__link" onClick={() => navigate("/b2b/register")}>
                    {t("b2bLogin.signUp")}
                  </button>
                </p>
              </form>
            )}

            <p className="auth-page__legal-text">
              <Trans
                t={t}
                i18nKey="shared.legal"
                components={{
                  terms: <button type="button" className="auth-form__link auth-form__link--muted" />,
                  privacy: <button type="button" className="auth-form__link auth-form__link--muted" />,
                }}
              />
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default B2BLoginPage;
